import seriesOficiales from '../core/data/seriesOficiales.json'

function toKey(value) {
  const txt = String(value || '')
  const slash = txt.match(/^(\d{2})\/(\d{4})$/)
  if (slash) return `${slash[2]}-${slash[1]}`
  const iso = txt.match(/^(\d{4})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}`
  return ''
}

function nextMonthKey(key) {
  const m = String(key || '').match(/^(\d{4})-(\d{2})$/)
  if (!m) return ''
  const y = Number(m[1])
  const mm = Number(m[2])
  const nm = mm + 1
  if (nm > 12) return `${y + 1}-01`
  return `${y}-${String(nm).padStart(2, '0')}`
}

function precedenceKey(tipo = 'PASIVA_BCRA_A14290') {
  const raw = String(tipo || '').toUpperCase().trim()
  if (raw === 'PASIVA_14290') return 'PASIVA_BCRA_A14290'
  if (raw === 'ACTIVA_JUZGADO') return 'ACTIVA_CARTERA_CARDOZO'
  if (seriesOficiales[raw]) return raw
  return 'PASIVA_BCRA_A14290'
}

function getMensualSerie(tipo = 'PASIVA_BCRA_A14290') {
  const key = precedenceKey(tipo)
  return seriesOficiales[key]?.mensual || {}
}

export const SERIES_OFICIALES = {
  PASIVA_BCRA_A14290: {
    fuente: seriesOficiales.PASIVA_BCRA_A14290?.fuente || 'BCRA',
    precedente: seriesOficiales.PASIVA_BCRA_A14290?.precedente || 'A 14.290',
    mensual: getMensualSerie('PASIVA_BCRA_A14290'),
  },
  PASIVA_PYME_PINTO: {
    fuente: seriesOficiales.PASIVA_PYME_PINTO?.fuente || 'BCRA',
    precedente: seriesOficiales.PASIVA_PYME_PINTO?.precedente || 'Pinto',
    mensual: getMensualSerie('PASIVA_PYME_PINTO'),
  },
  ACTIVA_CARTERA_CARDOZO: {
    fuente: seriesOficiales.ACTIVA_CARTERA_CARDOZO?.fuente || 'Banco Nacion/CSS',
    precedente: seriesOficiales.ACTIVA_CARTERA_CARDOZO?.precedente || 'Cardozo',
    mensual: getMensualSerie('ACTIVA_CARTERA_CARDOZO'),
  },
}

SERIES_OFICIALES.PASIVA_14290 = SERIES_OFICIALES.PASIVA_BCRA_A14290
SERIES_OFICIALES.ACTIVA_JUZGADO = SERIES_OFICIALES.ACTIVA_CARTERA_CARDOZO

const JURISDICCION_TASAS = {
  CABA_FEDERAL: { tasaDefault: 'PASIVA_BCRA_A14290', criterio: 'CFSS CABA', sala: 'General', vigenciaDesde: '1991-01' },
  CABA_CFSS_PINTO: { tasaDefault: 'PASIVA_PYME_PINTO', criterio: 'CFSS CABA', sala: 'Pinto', vigenciaDesde: '1991-01' },
  PROVINCIA_ACTIVA: { tasaDefault: 'ACTIVA_CARTERA_CARDOZO', criterio: 'Federal interior', sala: 'Cardozo', vigenciaDesde: '1991-01' },
  ANSeS_NACIONAL: { tasaDefault: 'PASIVA_BCRA_A14290', criterio: 'ANSeS administrativo', sala: 'Sede Central', vigenciaDesde: '1991-01' },
}

export function getTasaMensual(periodo, tipo = 'PASIVA_BCRA_A14290') {
  const key = toKey(periodo)
  if (!key) return 0
  const serie = getMensualSerie(tipo)
  return Number(serie[key] || 0)
}

export function calcularInteresRango(desde, hasta, serieInput) {
  const fromKey = toKey(desde)
  const toKeyVal = toKey(hasta)
  if (!fromKey || !toKeyVal) return 1
  let cursor = fromKey
  let factor = 1
  const serie = serieInput || {}
  while (cursor <= toKeyVal) {
    const tasa = Number(serie[cursor] || 0)
    factor *= (1 + tasa)
    const nxt = nextMonthKey(cursor)
    if (!nxt || nxt === cursor) break
    cursor = nxt
  }
  return factor
}

export function proyectarConInteresCompuesto(monto, periodo, tipo = 'PASIVA_BCRA_A14290', fechaCorte = null) {
  const serie = getMensualSerie(tipo)
  const parsedCorte = fechaCorte ? new Date(fechaCorte) : null
  const today = parsedCorte && !Number.isNaN(parsedCorte.getTime()) ? parsedCorte : new Date()
  const hasta = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  const factor = calcularInteresRango(periodo, hasta, serie)
  const capital = Number(monto || 0)
  if (!Number.isFinite(capital) || capital === 0) return 0
  return capital * factor
}

export function getSerieMetadata(tipo = 'PASIVA_BCRA_A14290', jurisdiccion = 'CABA_FEDERAL', fechaCalculo = null) {
  const profileKey = precedenceKey(tipo)
  const serieMeta = seriesOficiales[profileKey] || {}
  const perfil = JURISDICCION_TASAS[jurisdiccion] || JURISDICCION_TASAS.CABA_FEDERAL
  const d = fechaCalculo ? new Date(fechaCalculo) : new Date()
  const corte = Number.isNaN(d.getTime()) ? new Date() : d
  const corteTxt = corte.toLocaleDateString('es-AR')
  const cKey = `${corte.getFullYear()}-${String(corte.getMonth() + 1).padStart(2, '0')}`
  const vigente = cKey >= String(perfil.vigenciaDesde || '1991-01')
  return {
    fuente: serieMeta.fuente || 'Serie oficial',
    fechaCorte: corteTxt,
    jurisdiccion,
    criterio: perfil.criterio,
    sala: perfil.sala || 'N/D',
    precedente: serieMeta.precedente || profileKey,
    profileKey,
    vigente,
  }
}

export function resolveTasaForJurisdiccion(jurisdiccion = 'CABA_FEDERAL') {
  const profile = JURISDICCION_TASAS[jurisdiccion] || JURISDICCION_TASAS.CABA_FEDERAL
  return { jurisdiccion, ...profile }
}

export function getSerieStatus(tipo = 'PASIVA_BCRA_A14290') {
  const serie = getMensualSerie(tipo)
  const keys = Object.keys(serie).sort()
  const ultima = keys[keys.length - 1] || ''
  const now = new Date()
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const upToDate = Boolean(ultima && ultima >= currentKey)
  const ultimaMesAnio = ultima ? `${ultima.slice(5, 7)}/${ultima.slice(0, 4)}` : 'N/A'
  return { upToDate, ultimaKey: ultima, ultimaMesAnio, currentKey }
}

export const TASAS_DISPONIBLES = [
  { value: 'PASIVA_BCRA_A14290', label: 'Pasiva BCRA A 14.290' },
  { value: 'PASIVA_PYME_PINTO', label: 'Pasiva PyME (Pinto)' },
  { value: 'ACTIVA_CARTERA_CARDOZO', label: 'Activa Cartera General (Cardozo)' },
]

export const JURISDICCIONES_DISPONIBLES = [
  { value: 'ANSeS_NACIONAL', label: 'ANSeS Nacional (sede central)', tasaDefault: 'PASIVA_BCRA_A14290' },
  { value: 'CABA_FEDERAL', label: 'CABA - CFSS', tasaDefault: 'PASIVA_BCRA_A14290' },
  { value: 'CABA_CFSS_PINTO', label: 'CABA - CFSS (Pinto)', tasaDefault: 'PASIVA_PYME_PINTO' },
  { value: 'PROVINCIA_ACTIVA', label: 'Federal interior (Cardozo)', tasaDefault: 'ACTIVA_CARTERA_CARDOZO' },
]
